"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { format, endOfDay, endOfMonth, startOfDay, startOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  LayoutGrid,
  List,
  Package,
  PieChart,
  Plus,
} from "lucide-react";
import type { OrderOutput, OrderPaymentStatus, Pagination } from "@/interfaces";
import {
  OrderStatus,
  orderPaymentStatusTranslation,
  orderStatusTranslation,
} from "@/interfaces";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { cn } from "@/lib/utils";
import type { QueryParams } from "@/services/BaseService";
import { OrderService } from "@/services/OrderService";
import { numberToMoney } from "@/util/moneyConversion";
import { MonthYearFilter } from "../dashboard/components/dashboard/MonthYearFilter";
import { ProductsProfitChart } from "../dashboard/components/dashboard/ProductsProfitChart";
import { TopProductsChart } from "../dashboard/components/dashboard/TopProductsChart";
import { OrderCard } from "./components/OrderCard";
import { OrdersFilterBar, type OrdersPageFilters } from "./components/OrdersFilterBar";
import { OrderList } from "./components/OrderList";
import { OrderTotals } from "./components/OrderTotals";

type ViewMode = "card" | "list";
type OrdersTab = "orders" | "metrics" | "preparation";

interface PreparationOrderItem {
  orderId: string;
  customerName: string;
  preparationDate: string | Date;
  quantity: number;
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  totalAmount: number;
}

interface PreparationProductSummary {
  productId: string;
  productName: string;
  totalQuantity: number;
  doneQuantity: number;
  orderCount: number;
  orders: PreparationOrderItem[];
}

interface PreparationStatusProductSummary extends PreparationProductSummary {
  status: OrderStatus;
  statusQuantity: number;
  statusOrderCount: number;
  orders: PreparationOrderItem[];
}

type PreparationSummaryKey = "to_prepare" | "in_preparation" | "done";

const defaultPagination: Pagination = {
  page: 1,
  pageSize: 20,
  total: 0,
  pages: 0,
  links: {
    self: "",
  },
};

const preparationStatusOrder: OrderStatus[] = [
  OrderStatus.PENDING,
  OrderStatus.SCHEDULED,
  OrderStatus.IN_PREPARATION,
  OrderStatus.PREPARED,
  OrderStatus.DONE,
];

const preparationSummaryGroups: Array<{
  key: PreparationSummaryKey;
  label: string;
  statuses: OrderStatus[];
  numberClassName: string;
  surfaceClassName: string;
}> = [
  {
    key: "to_prepare",
    label: "A preparar",
    statuses: [OrderStatus.PENDING, OrderStatus.SCHEDULED],
    numberClassName: "text-amber-700",
    surfaceClassName: "border-amber-200 bg-amber-50",
  },
  {
    key: "in_preparation",
    label: "Em preparação",
    statuses: [OrderStatus.IN_PREPARATION],
    numberClassName: "text-blue-700",
    surfaceClassName: "border-blue-200 bg-blue-50",
  },
  {
    key: "done",
    label: "Prontos",
    statuses: [OrderStatus.PREPARED, OrderStatus.DONE],
    numberClassName: "text-green-700",
    surfaceClassName: "border-green-200 bg-green-50",
  },
];

function isViewMode(value: string): value is ViewMode {
  return value === "card" || value === "list";
}

function isOrdersTab(value: string): value is OrdersTab {
  return value === "orders" || value === "metrics" || value === "preparation";
}

function getCustomerName(order: OrderOutput) {
  if (typeof order.customer === "string") {
    return order.customer;
  }

  return order.customer?.name ?? `Pedido #${order.id.slice(0, 8)}`;
}

function isPreparationRelevantStatus(status: OrderStatus) {
  return preparationStatusOrder.includes(status);
}

function getPreparationProgressClassName(doneQuantity: number, totalQuantity: number) {
  if (totalQuantity <= 0 || doneQuantity <= 0) {
    return {
      badge: "bg-red-100 text-red-700 hover:bg-red-100",
      value: "text-red-600",
    };
  }

  if (doneQuantity >= totalQuantity) {
    return {
      badge: "bg-green-100 text-green-700 hover:bg-green-100",
      value: "text-green-600",
    };
  }

  return {
    badge: "bg-amber-100 text-amber-700 hover:bg-amber-100",
    value: "text-amber-600",
  };
}

function renderPreparationProductCard({
  sectionStatus,
  product,
  aggregateProduct,
  onSelect,
}: Readonly<{
  sectionStatus: OrderStatus;
  product: PreparationStatusProductSummary;
  aggregateProduct: PreparationProductSummary;
  onSelect: (product: PreparationProductSummary) => void;
}>) {
  const progressClassName = getPreparationProgressClassName(
    product.doneQuantity,
    product.totalQuantity
  );

  return (
    <button
      key={`${sectionStatus}-${product.productId}`}
      type="button"
      onClick={() => onSelect(aggregateProduct)}
      className="text-left"
    >
      <Card className="h-full border-gray-200 transition-all hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md dark:border-gray-800">
        <CardHeader className="space-y-3">
          <div className="space-y-1">
            <CardTitle className="line-clamp-2 text-lg">{product.productName}</CardTitle>
            <CardDescription>
              {product.orderCount} pedido(s) incluem este produto
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={progressClassName.badge}>
              {product.doneQuantity}/{product.totalQuantity} prontos
            </Badge>
            <Badge variant="outline">
              {product.statusQuantity} item(ns) neste status
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex items-end justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">Prontos / total</p>
            <p
              className={cn(
                "text-4xl font-bold tracking-tight",
                progressClassName.value
              )}
            >
              {product.doneQuantity}/{product.totalQuantity}
            </p>
          </div>
          <Badge variant="outline">Clique para ver pedidos</Badge>
        </CardContent>
      </Card>
    </button>
  );
}

function buildOrdersQueryParams(
  filters: OrdersPageFilters,
  page: number,
  pageSize: number,
  expand: string[]
): QueryParams {
  const queryParams: QueryParams = {
    expand,
    page,
    pageSize,
  };

  if (filters.customerId) queryParams.customerId = filters.customerId;
  if (filters.status) queryParams.status = filters.status;
  if (filters.paymentStatus) queryParams.paymentStatus = [filters.paymentStatus];
  if (filters.deliveryType) queryParams.deliveryType = filters.deliveryType;
  if (filters.startOrderDate) {
    queryParams.startOrderDate = startOfDay(filters.startOrderDate).toISOString();
  }
  if (filters.endOrderDate) {
    queryParams.endOrderDate = endOfDay(filters.endOrderDate).toISOString();
  }
  if (filters.tags?.length) queryParams.tags = filters.tags;

  return queryParams;
}

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  const { page, pageSize, total, pages } = pagination;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="mt-4 flex flex-col items-center justify-between gap-4 px-2 sm:flex-row">
      <div className="text-sm text-gray-500">
        Mostrando {total > 0 ? startItem : 0} a {endItem} de {total} pedidos
      </div>

      <div className="flex items-center gap-2">
        <div className="mr-4 flex items-center">
          <span className="mr-2 text-sm text-gray-500">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {pages === 0 ? (
              <Button
                variant="default"
                size="icon"
                className="h-8 w-8 bg-[#EE7B11] hover:bg-[#d06a0a]"
              >
                1
              </Button>
            ) : (
              Array.from({ length: Math.max(1, Math.min(pages, 5)) }, (_, i) => {
                let pageNumber: number;

                if (pages <= 5) {
                  pageNumber = i + 1;
                } else if (page <= 3) {
                  pageNumber = i + 1;
                } else if (page >= pages - 2) {
                  pageNumber = pages - 4 + i;
                } else {
                  pageNumber = page - 2 + i;
                }

                return (
                  <Button
                    key={pageNumber}
                    variant={page === pageNumber ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onPageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Button>
                );
              })
            )}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

function PreparationDateRangePicker({
  value,
  onChange,
}: Readonly<{
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
}>) {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(value);

  useEffect(() => {
    setDraftRange(value);
  }, [value]);

  const label = value?.from
    ? value.to
      ? `${format(value.from, "dd/MM/yyyy")} - ${format(value.to, "dd/MM/yyyy")}`
      : format(value.from, "dd/MM/yyyy")
    : "Selecionar intervalo";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="min-w-[280px] justify-start text-left font-normal"
        >
          <CalendarDays className="mr-2 h-4 w-4 text-[#EE7B11]" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="space-y-4 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Data inicial
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {draftRange?.from
                  ? format(draftRange.from, "dd/MM/yyyy")
                  : "Selecione"}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Data final
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {draftRange?.to ? format(draftRange.to, "dd/MM/yyyy") : "Selecione"}
              </p>
            </div>
          </div>

          <Calendar
            mode="range"
            selected={draftRange}
            onSelect={setDraftRange}
            numberOfMonths={2}
            locale={ptBR}
            defaultMonth={draftRange?.from ?? value?.from}
          />

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDraftRange(undefined)}
            >
              Limpar
            </Button>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDraftRange(value);
                  setOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className="bg-[#EE7B11] hover:bg-[#d06a0a]"
                onClick={() => {
                  onChange(draftRange);
                  setOpen(false);
                }}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Orders() {
  const navigate = useNavigate();
  const orderService = useRef(new OrderService()).current;
  const {
    isOpen,
    itemToDelete,
    open: openDeleteDialog,
    close: closeDeleteDialog,
  } = useDeleteConfirmation<string>();

  const today = new Date();
  const [activeTab, setActiveTab] = useState<OrdersTab>("orders");
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [currentFilters, setCurrentFilters] = useState<OrdersPageFilters>({});
  const [orders, setOrders] = useState<OrderOutput[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  const [metricsMonthYear, setMetricsMonthYear] = useState(
    `${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`
  );

  const [preparationRange, setPreparationRange] = useState<DateRange | undefined>({
    from: startOfMonth(today),
    to: endOfMonth(today),
  });
  const [preparationProducts, setPreparationProducts] = useState<
    PreparationProductSummary[]
  >([]);
  const [preparationProductsByStatus, setPreparationProductsByStatus] = useState<
    PreparationStatusProductSummary[]
  >([]);
  const [preparationLoading, setPreparationLoading] = useState(false);
  const [selectedPreparationProduct, setSelectedPreparationProduct] =
    useState<PreparationProductSummary | null>(null);

  const fetchOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);

      const response = await orderService.findAll(
        buildOrdersQueryParams(
          currentFilters,
          pagination.page,
          pagination.pageSize,
          ["customers", "tags", "products"]
        )
      );

      setOrders(response.data);
      setPagination((prev) => ({
        ...prev,
        ...response.pagination,
        page: pagination.page,
        pageSize: pagination.pageSize,
      }));
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error);
      setOrders([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        pages: 0,
      }));
    } finally {
      setOrdersLoading(false);
    }
  }, [currentFilters, orderService, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchPreparationOrders = useCallback(async () => {
    if (!preparationRange?.from || !preparationRange?.to) {
      setPreparationProducts([]);
      setPreparationProductsByStatus([]);
      return;
    }

    try {
      setPreparationLoading(true);

      const baseQueryParams = buildOrdersQueryParams(
        {
          startOrderDate: preparationRange.from,
          endOrderDate: preparationRange.to,
        },
        1,
        100,
        ["customers", "products"]
      );

      const firstResponse = await orderService.findAll(baseQueryParams);
      const allOrders = [...firstResponse.data];

      for (let page = 2; page <= firstResponse.pagination.pages; page += 1) {
        const nextResponse = await orderService.findAll({
          ...baseQueryParams,
          page,
        });
        allOrders.push(...nextResponse.data);
      }

      const productTotalsMap = new Map<string, PreparationProductSummary>();
      const statusProductsMap = new Map<string, PreparationStatusProductSummary>();

      allOrders.forEach((order) => {
        if (!isPreparationRelevantStatus(order.status)) {
          return;
        }

        const orderProductsMap = new Map<
          string,
          { productName: string; quantity: number }
        >();

        order.products.forEach((product) => {
          const existing = orderProductsMap.get(product.productId);

          if (existing) {
            existing.quantity += product.quantity;
            return;
          }

          orderProductsMap.set(product.productId, {
            productName: product.name,
            quantity: product.quantity,
          });
        });

        orderProductsMap.forEach((productData, productId) => {
          const totalSummary = productTotalsMap.get(productId) ?? {
            productId,
            productName: productData.productName,
            totalQuantity: 0,
            doneQuantity: 0,
            orderCount: 0,
            orders: [],
          };

          totalSummary.totalQuantity += productData.quantity;
          totalSummary.orderCount += 1;
          totalSummary.orders.push({
            orderId: order.id,
            customerName: getCustomerName(order),
            preparationDate: order.preparationDate,
            quantity: productData.quantity,
            status: order.status,
            paymentStatus: order.paymentStatus,
            totalAmount: order.totalAmount,
          });

          if (
            order.status === OrderStatus.PREPARED ||
            order.status === OrderStatus.DONE
          ) {
            totalSummary.doneQuantity += productData.quantity;
          }

          productTotalsMap.set(productId, totalSummary);

          const statusKey = `${order.status}:${productId}`;
          const statusSummary = statusProductsMap.get(statusKey) ?? {
            ...totalSummary,
            status: order.status,
            statusQuantity: 0,
            statusOrderCount: 0,
            orders: [],
          };

          statusSummary.productName = productData.productName;
          statusSummary.totalQuantity = totalSummary.totalQuantity;
          statusSummary.doneQuantity = totalSummary.doneQuantity;
          statusSummary.orderCount = totalSummary.orderCount;
          statusSummary.statusQuantity += productData.quantity;
          statusSummary.statusOrderCount += 1;
          statusSummary.orders.push({
            orderId: order.id,
            customerName: getCustomerName(order),
            preparationDate: order.preparationDate,
            quantity: productData.quantity,
            status: order.status,
            paymentStatus: order.paymentStatus,
            totalAmount: order.totalAmount,
          });

          statusProductsMap.set(statusKey, statusSummary);
        });
      });

      const aggregatedProducts = [...productTotalsMap.values()].sort(
        (a, b) => b.totalQuantity - a.totalQuantity
      );

      const groupedProducts = [...statusProductsMap.values()]
        .map((product) => ({
          ...product,
          totalQuantity:
            productTotalsMap.get(product.productId)?.totalQuantity ?? product.totalQuantity,
          doneQuantity:
            productTotalsMap.get(product.productId)?.doneQuantity ?? product.doneQuantity,
          orderCount:
            productTotalsMap.get(product.productId)?.orderCount ?? product.orderCount,
        }))
        .sort((a, b) => {
          const statusDiff =
            preparationStatusOrder.indexOf(a.status) -
            preparationStatusOrder.indexOf(b.status);

          if (statusDiff !== 0) return statusDiff;

          return b.statusQuantity - a.statusQuantity;
        });

      setPreparationProducts(aggregatedProducts);
      setPreparationProductsByStatus(groupedProducts);
    } catch (error) {
      console.error("Erro ao buscar compilado de preparação:", error);
      setPreparationProducts([]);
      setPreparationProductsByStatus([]);
    } finally {
      setPreparationLoading(false);
    }
  }, [orderService, preparationRange]);

  useEffect(() => {
    if (activeTab === "preparation") {
      fetchPreparationOrders();
    }
  }, [activeTab, fetchPreparationOrders]);

  const handleFilterChange = (filters: OrdersPageFilters) => {
    setCurrentFilters(filters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDelete = (id: string) => {
    openDeleteDialog(id);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setOrdersLoading(true);
      await orderService.delete(itemToDelete);
      await fetchOrders();
    } finally {
      setOrdersLoading(false);
      closeDeleteDialog();
    }
  };

  const preparationTotalQuantity = preparationProducts.reduce(
    (total, item) => total + item.totalQuantity,
    0
  );
  const preparationDoneQuantity = preparationProducts.reduce(
    (total, item) => total + item.doneQuantity,
    0
  );
  const preparationStatusTotals = preparationSummaryGroups.map((group) => ({
    ...group,
    quantity: preparationProductsByStatus.reduce((total, product) => {
      if (!group.statuses.includes(product.status)) {
        return total;
      }

      return total + product.statusQuantity;
    }, 0),
  }));
  const preparationSections = preparationStatusOrder
    .map((status) => ({
      status,
      label: orderStatusTranslation[status],
      products: preparationProductsByStatus.filter((product) => product.status === status),
    }))
    .filter((section) => section.products.length > 0);
  const preparationProductsMap = new Map(
    preparationProducts.map((product) => [product.productId, product] as const)
  );

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      page: 1,
    }));
  };

  const navigateToOrder = (orderId?: string) => {
    const targetRoute = orderId ? `/pedidos/${orderId}/` : "/pedidos/novo";
    navigate(targetRoute);
  };

  const navigateToPreOrders = () => {
    navigate("/pre-pedidos");
  };

  const renderOrdersContent = () => {
    const handleViewModeChange = (value: string) => {
      if (isViewMode(value)) {
        setViewMode(value);
      }
    };

    if (ordersLoading) {
      return <LoadingSpinner />;
    }

    return (
      <div className="space-y-6">
        <OrderTotals orders={orders} />

        <OrdersFilterBar filters={currentFilters} onChange={handleFilterChange} />

        <div className="flex justify-end">
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={handleViewModeChange}
          >
            <ToggleGroupItem
              value="card"
              aria-label="Visualização em cards"
              className="h-11 px-3"
            >
              <LayoutGrid className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Cards</span>
            </ToggleGroupItem>
            <ToggleGroupItem
              value="list"
              aria-label="Visualização em lista"
              className="h-11 px-3"
            >
              <List className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Lista</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        {orders.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-12 text-center dark:border-gray-800 dark:bg-gray-900/50">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
              <Package className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
              Nenhum pedido encontrado
            </h3>
            <p className="mx-auto max-w-md text-gray-500 dark:text-gray-400">
              Tente ajustar os filtros ou criar um novo pedido para começar.
            </p>
          </div>
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {orders.map((order) => (
              <div
                key={order.id}
                onClick={() => navigateToOrder(order.id)}
                role="button"
                className="w-full min-w-[280px]"
              >
                <OrderCard
                  order={order}
                  onDelete={() => handleDelete(order.id)}
                  className="h-full"
                />
              </div>
            ))}
          </div>
        ) : (
          <OrderList
            orders={orders}
            onDelete={handleDelete}
            onSelect={navigateToOrder}
          />
        )}

        {orders.length > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    );
  };

  const renderPreparationContent = () => {
    const rangeStart = preparationRange?.from;
    const rangeEnd = preparationRange?.to;
    const hasPreparationRange = Boolean(rangeStart && rangeEnd);
    const preparationRangeLabel =
      rangeStart && rangeEnd
        ? `Período: ${format(rangeStart, "dd/MM/yyyy")} até ${format(rangeEnd, "dd/MM/yyyy")}`
        : "Selecione um intervalo para visualizar os produtos.";
    let preparationContent;

    if (preparationLoading) {
      preparationContent = <LoadingSpinner />;
    } else if (!hasPreparationRange) {
      preparationContent = (
        <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
          Selecione um intervalo de datas para carregar os produtos.
        </div>
      );
    } else if (preparationProducts.length === 0) {
      preparationContent = (
        <div className="rounded-2xl border border-dashed border-gray-300 p-10 text-center text-gray-500">
          Nenhum produto encontrado para o período selecionado.
        </div>
      );
    } else {
      preparationContent = (
        <div className="space-y-6">
          {preparationSections.map((section) => (
            <section key={section.status} className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="outline" className="px-3 py-1 text-sm font-medium">
                  {section.label}
                </Badge>
                <p className="text-sm text-gray-500">
                  {section.products.length} produto(s) neste status
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {section.products.map((product) =>
                  renderPreparationProductCard({
                    sectionStatus: section.status,
                    product,
                    aggregateProduct:
                      preparationProductsMap.get(product.productId) ?? product,
                    onSelect: setSelectedPreparationProduct,
                  })
                )}
              </div>
            </section>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <Card className="border-gray-200 shadow-sm dark:border-gray-800">
          <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#EE7B11]" />
                Preparação por produto
              </CardTitle>
              <CardDescription>
                Consolidação dos produtos a preparar no período selecionado.
              </CardDescription>
            </div>

            <PreparationDateRangePicker
              value={preparationRange}
              onChange={setPreparationRange}
            />
          </CardHeader>
          <CardContent className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex-1 space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                {preparationStatusTotals.map((group) => (
                  <div
                    key={group.key}
                    className={cn(
                      "rounded-2xl border px-4 py-3",
                      group.surfaceClassName
                    )}
                  >
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {group.label}
                    </p>
                    <div className="mt-2 flex items-end gap-2">
                      <span
                        className={cn(
                          "text-4xl font-bold leading-none",
                          group.numberClassName
                        )}
                      >
                        {group.quantity}
                      </span>
                      <span className="pb-1 text-sm text-gray-500">itens</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <Badge className="bg-orange-100 px-3 py-1 text-orange-700 hover:bg-orange-100">
                  {preparationTotalQuantity} item(ns) no período
                </Badge>
                <Badge variant="outline" className="px-3 py-1">
                  {preparationProducts.length} produto(s) no compilado
                </Badge>
                <Badge variant="outline" className="px-3 py-1">
                  {preparationDoneQuantity}/{preparationTotalQuantity} item(ns) prontos
                </Badge>
              </div>
            </div>
            <p className="text-sm text-gray-500">{preparationRangeLabel}</p>
          </CardContent>
        </Card>

        {preparationContent}

        <Dialog
          open={Boolean(selectedPreparationProduct)}
          onOpenChange={(open) => {
            if (!open) setSelectedPreparationProduct(null);
          }}
        >
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                Pedidos com: {selectedPreparationProduct?.productName}
              </DialogTitle>
              <DialogDescription>
                Lista de todos os pedidos do período selecionado que incluem este
                produto.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {selectedPreparationProduct?.orders.map((order) => (
                <div
                  key={`${selectedPreparationProduct.productId}-${order.orderId}`}
                  className="rounded-xl border border-gray-200 p-4 dark:border-gray-800"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-1">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        {order.customerName}
                      </p>
                      <p className="text-sm text-gray-500">
                        Preparação:{" "}
                        {format(new Date(order.preparationDate), "dd/MM/yyyy")}
                      </p>
                      <p className="text-sm text-gray-500">
                        Quantidade no pedido: {order.quantity}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 lg:items-end">
                      <div className="flex items-center gap-2 lg:justify-end">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Status do pagamento:
                        </p>
                        <Badge
                          className={cn(
                            "w-fit",
                            order.paymentStatus === "PAID" &&
                              "bg-green-100 text-green-700 hover:bg-green-100",
                            order.paymentStatus === "PENDING" &&
                              "bg-yellow-100 text-yellow-700 hover:bg-yellow-100",
                            order.paymentStatus === "PARTIALLY_PAID" &&
                              "bg-orange-100 text-orange-700 hover:bg-orange-100"
                          )}
                        >
                          {orderPaymentStatusTranslation[order.paymentStatus]}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 lg:justify-end">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Status do pedido:
                        </p>
                        <Badge variant="outline" className="w-fit">
                          {orderStatusTranslation[order.status]}
                        </Badge>
                      </div>
                      <p className="text-sm font-semibold text-green-700">
                        {numberToMoney(order.totalAmount)}
                      </p>
                      <Button
                        size="sm"
                        className="bg-[#EE7B11] hover:bg-[#d06a0a]"
                        onClick={() => {
                          setSelectedPreparationProduct(null);
                          navigateToOrder(order.orderId);
                        }}
                      >
                        Ver pedido
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-[1600px] p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Pedidos
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Gestão operacional, métricas e preparação em uma única tela.
          </p>
        </div>

        <div className="flex w-full flex-wrap gap-3 sm:w-auto">
          <Button
            onClick={navigateToPreOrders}
            size="sm"
            variant="outline"
            className="border-[#EE7B11] text-[#EE7B11] hover:bg-orange-50 hover:text-[#d06a0a] hover:border-[#d06a0a]"
          >
            <FileText className="mr-2 h-4 w-4" />
            <span>Pré-pedidos</span>
            <ExternalLink className="ml-1.5 h-3.5 w-3.5 opacity-70" />
          </Button>

          <Button
            onClick={() => navigateToOrder()}
            size="sm"
            className="bg-[#EE7B11] text-white hover:bg-[#d06a0a]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Cadastrar Pedido
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (isOrdersTab(value)) {
            setActiveTab(value);
          }
        }}
        className="w-full"
      >
        <TabsList className="mb-6 grid h-auto w-full grid-cols-3 bg-orange-50 p-1 sm:w-[520px]">
          <TabsTrigger value="orders" className="gap-2">
            <LayoutGrid className="h-4 w-4" />
            Pedidos
          </TabsTrigger>
          <TabsTrigger value="metrics" className="gap-2">
            <PieChart className="h-4 w-4" />
            Métricas
          </TabsTrigger>
          <TabsTrigger value="preparation" className="gap-2">
            <Package className="h-4 w-4" />
            Preparação
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-0">
          {renderOrdersContent()}
        </TabsContent>

        <TabsContent value="metrics" className="mt-0 space-y-6">
          <Card className="border-0 bg-gradient-to-r from-orange-500 to-orange-600 shadow-sm">
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-white">
                  <BarChart3 className="h-5 w-5 text-white" />
                  Métricas de vendas
                </CardTitle>
                <CardDescription className="text-orange-100">
                  Os mesmos gráficos da aba de vendas do dashboard, por mês e ano.
                </CardDescription>
              </div>

              <MonthYearFilter
                onFilter={setMetricsMonthYear}
                initialMonth={metricsMonthYear.split("/")[0]}
                initialYear={metricsMonthYear.split("/")[1]}
              />
            </CardHeader>
          </Card>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ProductsProfitChart monthYear={metricsMonthYear} />
            <TopProductsChart monthYear={metricsMonthYear} limit={10} />
          </div>
        </TabsContent>

        <TabsContent value="preparation" className="mt-0">
          {renderPreparationContent()}
        </TabsContent>
      </Tabs>

      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirmar Exclusão de Pedido"
        message="Tem certeza que deseja excluir este pedido? Esta ação não pode ser desfeita."
        itemName={`Pedido #${itemToDelete?.substring(0, 8)}`}
      />
    </div>
  );
}

export default Orders;

